<template>
  <div class="chart-card">
    <div class="chart-card__header">
      <div>
        <h3 class="chart-card__title">{{ title }}</h3>
        <p class="chart-card__subtitle">{{ subtitle }}</p>
      </div>
      <div class="chart-card__tabs">
        <button
          v-for="(tab, index) in tabs"
          :key="index"
          class="chart-card__tab"
          :class="{ 'chart-card__tab--active': activeTab === index }"
          @click="$emit('tab-change', index)"
        >
          {{ tab }}
        </button>
      </div>
    </div>
    <div class="chart-card__body">
      <apexchart
        v-if="hasData"
        :type="chartType"
        :height="height"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div v-else class="chart-card__empty">
        <font-awesome-icon icon="chart-bar" class="chart-card__empty-icon" />
        <p>Ingen data tilgjengelig</p>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import moment from 'moment';

export default {
  name: 'SalesTrendChart',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    title: {
      type: String,
      default: 'Salgstrend'
    },
    subtitle: {
      type: String,
      default: ''
    },
    labels: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    tabs: {
      type: Array,
      default: () => ['30 dager', 'Månedlig']
    },
    activeTab: {
      type: Number,
      default: 0
    },
    chartType: {
      type: String,
      default: 'area' // 'bar', 'area', 'line'
    },
    height: {
      type: Number,
      default: 350
    },
    color: {
      type: String,
      default: '#B30E10'
    },
    isMonthlyView: {
      type: Boolean,
      default: false
    }
  },
  emits: ['tab-change'],
  computed: {
    hasData() {
      return this.data && this.data.length > 0;
    },
    chartSeries() {
      return [{
        name: 'Salg',
        data: this.data.map(val => val < 0 ? 0 : val)
      }];
    },
    chartOptions() {
      return {
        chart: {
          type: this.chartType,
          fontFamily: 'inherit',
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 600
          }
        },
        colors: [this.color],
        fill: {
          type: this.chartType === 'area' ? 'gradient' : 'solid',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 90, 100]
          }
        },
        stroke: {
          curve: 'smooth',
          width: this.chartType === 'area' ? 3 : 0
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: this.labels,
          labels: {
            style: {
              colors: 'var(--bo-muted)',
              fontSize: '11px'
            },
            formatter: (value, timestamp, opts) => {
              if (this.isMonthlyView) {
                return value;
              }
              // Show every 5th label for daily view (30 days / 5 = 6 labels)
              const index = opts?.i ?? 0;
              if (index % 5 !== 0) {
                return '';
              }
              const date = moment(value);
              return date.isValid() ? date.format('DD.MM') : value;
            },
            rotate: 0
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: 'var(--bo-muted)',
              fontSize: '12px'
            },
            offsetX: -15,
            formatter: (val) => {
              if (val >= 1000) {
                return (val / 1000).toFixed(0) + 'k';
              }
              return val.toFixed(0);
            }
          }
        },
        grid: {
          borderColor: 'var(--bo-surface-2)',
          strokeDashArray: 4,
          xaxis: {
            lines: {
              show: false
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => {
              return new Intl.NumberFormat('nb-NO').format(val) + ' kr';
            }
          },
          x: {
            formatter: (val) => {
              if (this.isMonthlyView) {
                return val;
              }
              const date = moment(this.labels[val - 1]);
              return date.isValid() ? date.format('DD.MM.YYYY') : val;
            }
          }
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: '60%'
          }
        }
      };
    }
  }
};
</script>

<style scoped>
.chart-card {
  background: var(--bo-surface);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.chart-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.chart-card__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--bo-ink);
  margin: 0 0 4px 0;
}

.chart-card__subtitle {
  font-size: 13px;
  color: var(--bo-muted);
  margin: 0;
}

.chart-card__tabs {
  display: flex;
  gap: 8px;
  background: var(--bo-surface-2);
  padding: 4px;
  border-radius: 8px;
}

.chart-card__tab {
  padding: 8px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--bo-muted);
  cursor: pointer;
  transition: all 0.2s ease;
}

.chart-card__tab:hover {
  color: var(--bo-ink);
}

.chart-card__tab--active {
  background: var(--bo-surface);
  color: var(--bo-alert-ink);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chart-card__body {
  min-height: 300px;
}

.chart-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: var(--bo-muted-2);
}

.chart-card__empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

@media (max-width: 768px) {
  .chart-card {
    padding: 16px 10px;
  }
}
</style>
